/**
 * SC-763 — « ce terminal tourne-t-il encore sur l'ancien paquet ? »
 *
 * `site-commande` est du React + Vite : il n'hérite pas du sondage de version
 * natif de SvelteKit qui protège les deux tableaux de bord
 * (`ignite-admin/svelte.config.js`, `ignite-owner/svelte.config.js` —
 * `version: { pollInterval: 60_000 }`). Constaté en production le 2026-08-27 :
 * après un déploiement, un onglet déjà ouvert continue d'exécuter l'ancien
 * bundle jusqu'à ce qu'un humain rafraîchisse, sans erreur ni indice à l'écran.
 *
 * Deux valeurs, et c'est leur écart qui est le signal :
 *   - celle de l'onglet, figée dans l'`index.html` qui l'a démarré (balise
 *     <meta name="build-version">, posée par le plugin `buildVersionStamp` de
 *     vite.config.ts) ;
 *   - celle du site en ligne, relue dans `/version.json`.
 *
 * TOUT échoue ouvert : pas de balise, pas de fichier, réponse illisible ⇒ on ne
 * sait pas, donc on ne dit rien. Un point de version instable ne doit jamais
 * empêcher un serveur de prendre une commande.
 */

/** L'empreinte du paquet que CET onglet exécute. `''` quand elle est absente. */
export function currentBuildVersion(): string {
  const meta = document.querySelector('meta[name="build-version"]');
  return meta?.getAttribute('content')?.trim() ?? '';
}

/**
 * L'empreinte servie en ce moment par le site, ou `null` si on n'a pas pu la
 * lire — ce qui n'est PAS un signal de péremption.
 *
 * ⚠ Le piège que cette fonction existe pour éviter : `GET /version.json`
 * répondait **200 avec l'index.html** en production, à cause du repli SPA de
 * nginx (`try_files $uri $uri/ /index.html`). Une implémentation naïve
 * « si 200 alors je compare » croit donc toujours réussir, et compare des pages
 * HTML entre elles. Le `content-type` est vérifié pour cette raison précise ;
 * `nginx/default.conf` sort en plus `/version.json` du repli.
 */
export async function fetchServedBuildVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store', signal });
    if (!response.ok) return null;
    if (!(response.headers.get('content-type') ?? '').includes('application/json')) return null;
    const payload: unknown = await response.json();
    const version = (payload as { version?: unknown } | null)?.version;
    return typeof version === 'string' && version.trim() ? version.trim() : null;
  } catch {
    return null;
  }
}
